import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { queryClient } from '@/lib/query-client';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ApiErrorBridge } from '@/components/ApiErrorBridge';
import { ToastContainer } from '@/components/ui/toast';
import { BookingsPage } from '@/pages/BookingsPage';
import { PublicBookingPage } from '@/pages/PublicBookingPage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CandidatesPage } from '@/pages/CandidatesPage';
import { CandidateProfilePage } from '@/pages/CandidateProfilePage';
import { JobsPage } from '@/pages/JobsPage';
import { JobDetailPage } from '@/pages/JobDetailPage';
import { CreateJobPage } from '@/pages/CreateJobPage';
import { EditJobPage } from '@/pages/EditJobPage';
import { CompaniesPage } from '@/pages/CompaniesPage';
import { CompanyDetailPage } from '@/pages/CompanyDetailPage';
import { CreateCompanyPage } from '@/pages/CreateCompanyPage';
import { EditCompanyPage } from '@/pages/EditCompanyPage';
import { PipelinePage } from '@/pages/PipelinePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { CreateCandidatePage } from '@/pages/CreateCandidatePage';
import { EditCandidatePage } from '@/pages/EditCandidatePage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { PlacementsPage } from '@/pages/PlacementsPage';
import { PlacementDetailPage } from '@/pages/PlacementDetailPage';
import { InvoicesPage } from '@/pages/InvoicesPage';
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage';
import { InterviewsPage } from '@/pages/InterviewsPage';
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import { PortalDashboardPage } from '@/pages/portal/PortalDashboardPage';
import { PortalJobsPage } from '@/pages/portal/PortalJobsPage';
import { PortalSubmissionsPage } from '@/pages/portal/PortalSubmissionsPage';
import { PortalLayout } from '@/components/portal/PortalLayout';
import { PortalAuthProvider } from '@/contexts/PortalAuthContext';

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <ApiErrorBridge />
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/book/manage" element={<PublicBookingPage manage />} />
                <Route path="/book/:slug" element={<PublicBookingPage />} />
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/candidates" element={<CandidatesPage />} />
                  <Route path="/candidates/new" element={<CreateCandidatePage />} />
                  <Route path="/candidates/:id" element={<CandidateProfilePage />} />
                  <Route path="/candidates/:id/edit" element={<EditCandidatePage />} />
                  <Route path="/jobs" element={<JobsPage />} />
                  <Route path="/jobs/new" element={<CreateJobPage />} />
                  <Route path="/jobs/:id" element={<JobDetailPage />} />
                  <Route path="/jobs/:id/edit" element={<EditJobPage />} />
                  <Route path="/companies" element={<CompaniesPage />} />
                  <Route path="/companies/new" element={<CreateCompanyPage />} />
                  <Route path="/companies/:id" element={<CompanyDetailPage />} />
                  <Route path="/companies/:id/edit" element={<EditCompanyPage />} />
                  <Route path="/pipeline" element={<PipelinePage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/placements" element={<PlacementsPage />} />
                  <Route path="/placements/:id" element={<PlacementDetailPage />} />
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                  <Route path="/interviews" element={<InterviewsPage />} />
                  <Route path="/bookings" element={<BookingsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>

                {/* Client Portal routes */}
                <Route path="/portal/login" element={
                  <PortalAuthProvider>
                    <PortalLoginPage />
                  </PortalAuthProvider>
                } />
                <Route element={
                  <PortalAuthProvider>
                    <PortalLayout />
                  </PortalAuthProvider>
                }>
                  <Route path="/portal" element={<PortalDashboardPage />} />
                  <Route path="/portal/jobs" element={<PortalJobsPage />} />
                  <Route path="/portal/submissions" element={<PortalSubmissionsPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
            <ToastContainer />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
